import { loadStripe, Stripe } from "@stripe/stripe-js"

export interface StripeLoadConfig {
  publishableKey: string
  maxRetries?: number
  forceError?: boolean
  forceSlow?: boolean
}

export interface StripeLoadResult {
  stripe: Stripe | null
  error: Error | null
  attempts: number
}

/**
 * Load Stripe with retry mechanism and exponential backoff
 */
export const loadStripeWithRetry = async (
  config: StripeLoadConfig,
): Promise<StripeLoadResult> => {
  const {
    publishableKey,
    maxRetries = 3,
    forceError = false,
    forceSlow = false,
  } = config

  let lastError: Error | null = null

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // Development testing helpers
      if (forceError) {
        throw new Error("Simulated Stripe loading failure for testing")
      }

      if (forceSlow) {
        await new Promise((resolve) => setTimeout(resolve, 5000))
      }

      const stripe = await loadStripe(publishableKey)

      if (stripe) {
        return { stripe, error: null, attempts: attempt + 1 }
      } else {
        throw new Error("Stripe loaded but returned null")
      }
    } catch (error) {
      lastError = error instanceof Error ? error : new Error("Unknown error")
      console.warn(`Stripe loading attempt ${attempt + 1} failed:`, lastError)

      // Don't wait after the last attempt
      if (attempt < maxRetries) {
        const delay = Math.pow(2, attempt) * 1000 // 1s, 2s, 4s
        await new Promise((resolve) => setTimeout(resolve, delay))
      }
    }
  }

  console.error("Failed to load Stripe after all retry attempts")
  return { stripe: null, error: lastError, attempts: maxRetries + 1 }
}

/**
 * Get Stripe configuration from environment variables
 */
export const getStripeConfig = (): StripeLoadConfig => {
  const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY

  if (!publishableKey) {
    throw new Error("VITE_STRIPE_PUBLISHABLE_KEY is not defined")
  }

  return {
    publishableKey,
    forceError: import.meta.env.VITE_FORCE_STRIPE_ERROR === "true",
    forceSlow: import.meta.env.VITE_FORCE_STRIPE_SLOW === "true",
  }
}
